import ChainLogo from '@components/ChainLogo'
import SearchBar from '@components/SearchBar/SearchBar'
import { TokenList } from '@components/TokenList/TokenList'
import { useTokenPicker } from '@plug-core/hooks/useTokenPicker'
import { PlugToken } from '@plug-core/interfaces/common'
import { TokenBalance } from '@plug-core/providers/BalanceProvider'
import { ListGroup, ListRow, Sheet, cn } from '@ui'
import { getWindowDimensions } from '@utils/windows'
import React, { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface TokenPickerProps {
  visible: boolean
  title: string
  tokensForSelect: PlugToken[]
  preselectChain?: string
  tokensToBalances: (tokens: PlugToken[]) => TokenBalance[]
  onClose: () => void
  onTokenSelect: (token: PlugToken | undefined) => void
}

const Picker = ({
  type,
  children,
}: {
  type: 'token' | 'chain'
  children: (searchTerm: string) => ReactNode
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('')

  const { t } = useTranslation()

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
      <SearchBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        placeholder={type === 'token' ? t('common.enterTokenName') : t('common.enterChainName')}
        autoFocus
      />

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {children(searchTerm.toLowerCase())}
      </div>
    </div>
  )
}

const chipClasses = (selected: boolean) =>
  cn(
    'grid size-[45px] shrink-0 cursor-pointer place-items-center rounded-sm border-[1.5px] bg-transparent p-0 font-text text-[15px] font-bold text-fg transition-colors duration-150',
    'hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
    selected ? 'border-hairline-strong bg-surface-2' : 'border-hairline'
  )

const TokenPicker = ({
  visible,
  title,
  tokensForSelect,
  preselectChain,
  onClose,
  onTokenSelect,
  tokensToBalances,
}: TokenPickerProps): JSX.Element => {
  const {
    type,
    setType,
    chainMode,
    getChainInfo,
    onSelect,
    onChainSelect,
    chains,
    visibleChains,
    visibleChainCount,
    showChains,
    searchTokens,
    searchChains,
    filteredTokens,
  } = useTokenPicker({ preselectChain, onTokenSelect, onClose, tokensForSelect })

  const renderChain = (chain: string) => (
    <ListRow
      key={chain}
      leading={<ChainLogo chain={chain} size={40} />}
      primary={getChainInfo(chain).symbol}
      secondary={getChainInfo(chain).name}
      selected={chainMode === chain}
      onClick={() => onChainSelect(chain)}
    />
  )

  const isChainSelected = (chain: string) =>
    type === 'chain' ? chain === 'other' : chainMode === chain

  return (
    <Sheet
      open={visible}
      onClose={onClose}
      title={type === 'chain' ? 'Select chain' : title}
      closable
    >
      <div className="flex flex-col gap-3" style={{ height: getWindowDimensions().height * 0.6 }}>
        <div className="flex flex-wrap gap-1.5">
          {showChains &&
            visibleChains.map((chain) => (
              <button
                key={chain}
                type="button"
                aria-label={getChainInfo(chain).name}
                aria-pressed={isChainSelected(chain)}
                onClick={() => onChainSelect(chain)}
                className={chipClasses(isChainSelected(chain))}
              >
                <ChainLogo chain={chain} size={32} />
              </button>
            ))}

          {chains.length > visibleChainCount && (
            <button
              type="button"
              aria-pressed={isChainSelected('other')}
              onClick={() => setType('chain')}
              className={cn(chipClasses(isChainSelected('other')), 'tabular')}
            >
              +{chains.length - visibleChainCount}
            </button>
          )}
        </div>

        {type === 'token' && (
          <Picker type={type}>
            {(searchTerm) => (
              <TokenList
                balances={tokensToBalances(searchTokens(searchTerm, filteredTokens))}
                onTokenClick={(token) => onSelect(token.token)}
                showPriceChange={false}
              />
            )}
          </Picker>
        )}

        {type === 'chain' && (
          <Picker type={type}>
            {(searchTerm) => <ListGroup>{searchChains(searchTerm).map(renderChain)}</ListGroup>}
          </Picker>
        )}
      </div>
    </Sheet>
  )
}

export default TokenPicker
